import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Plus, Heart, Sparkles, Crown, Cross, Smile, Coffee, Video, Mic } from "lucide-react";
import { cn } from "@/lib/utils";

interface CreateExhibitionBoothModalProps {
  cityId: string;
  cityName: string;
  countryCode: string;
  countryName: string;
  onCreate: (params: {
    name: string;
    topic: string;
    description: string;
    city_id: string;
    country_code: string;
    exhibition_intent: string;
    language: string;
    max_participants: number;
    is_video_enabled: boolean;
  }) => Promise<string | null>;
}

const INTENTS = [
  { id: "flirting", label: "Flirting", icon: Heart, color: "from-pink-500 to-rose-500", description: "Light & playful vibes" },
  { id: "serious", label: "Serious", icon: Sparkles, color: "from-purple-500 to-violet-500", description: "Looking for real connection" },
  { id: "elite", label: "Elite", icon: Crown, color: "from-amber-500 to-yellow-500", description: "Premium & exclusive" },
  { id: "faith", label: "Faith", icon: Cross, color: "from-blue-500 to-cyan-500", description: "Shared beliefs" },
  { id: "fun", label: "Fun", icon: Smile, color: "from-green-500 to-emerald-500", description: "Just having a good time" },
  { id: "casual", label: "Casual", icon: Coffee, color: "from-orange-500 to-amber-500", description: "No pressure, just vibes" },
];

const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "es", label: "Spanish" },
  { code: "pt", label: "Portuguese" },
  { code: "fr", label: "French" },
  { code: "de", label: "German" },
  { code: "ja", label: "Japanese" },
  { code: "ko", label: "Korean" },
  { code: "zh", label: "Chinese" },
];

const CreateExhibitionBoothModal: React.FC<CreateExhibitionBoothModalProps> = ({
  cityId,
  cityName,
  countryCode,
  countryName,
  onCreate,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [name, setName] = useState("");
  const [topic, setTopic] = useState("");
  const [description, setDescription] = useState("");
  const [intent, setIntent] = useState("casual");
  const [language, setLanguage] = useState("en");
  const [maxParticipants, setMaxParticipants] = useState([30]);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);

  const handleCreate = async () => {
    if (!name.trim() || !topic.trim()) return;

    setIsCreating(true);
    const boothId = await onCreate({
      name: name.trim(),
      topic: topic.trim(),
      description: description.trim(),
      city_id: cityId,
      country_code: countryCode,
      exhibition_intent: intent,
      language,
      max_participants: maxParticipants[0],
      is_video_enabled: isVideoEnabled,
    });

    setIsCreating(false);

    if (boothId) {
      setIsOpen(false);
      // Reset form
      setName("");
      setTopic("");
      setDescription("");
      setIntent("casual");
      setLanguage("en");
      setMaxParticipants([30]);
      setIsVideoEnabled(true);
    }
  };

  const selectedIntent = INTENTS.find(i => i.id === intent);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          className="rounded-full bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 shadow-lg"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Booth
        </Button>
      </DialogTrigger>

      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">Create Exhibition Booth</DialogTitle>
          <p className="text-sm text-muted-foreground">
            {cityName}, {countryName}
          </p>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Booth Name */}
          <div className="space-y-2">
            <Label htmlFor="name">Booth Name *</Label>
            <Input
              id="name"
              placeholder="e.g., Golden Hearts Lounge"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={50}
            />
          </div>

          {/* Topic */}
          <div className="space-y-2">
            <Label htmlFor="topic">Topic *</Label>
            <Input
              id="topic"
              placeholder="e.g., Late Night Conversations"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              maxLength={100}
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              placeholder="Tell people what this booth is about..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={250}
              rows={3}
            />
          </div>

          {/* Intent Selection */}
          <div className="space-y-3">
            <Label>Booth Intent *</Label>
            <div className="grid grid-cols-2 gap-2">
              {INTENTS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setIntent(item.id)}
                  className={cn(
                    "p-3 rounded-xl border-2 transition-all text-left",
                    intent === item.id
                      ? `border-pink-500 bg-gradient-to-r ${item.color} text-white`
                      : "border-border bg-muted/50 hover:border-pink-300"
                  )}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <item.icon className="w-4 h-4" />
                    <span className="font-medium text-sm">{item.label}</span>
                  </div>
                  <p className={cn(
                    "text-xs",
                    intent === item.id ? "text-white/80" : "text-muted-foreground"
                  )}>
                    {item.description}
                  </p>
                </button>
              ))}
            </div>
          </div>

          {/* Language */}
          <div className="space-y-2">
            <Label>Language</Label>
            <div className="flex flex-wrap gap-2">
              {LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  type="button"
                  onClick={() => setLanguage(lang.code)}
                  className={cn(
                    "px-3 py-1.5 rounded-full text-sm transition-all",
                    language === lang.code
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground hover:bg-muted/80"
                  )}
                >
                  {lang.label}
                </button>
              ))}
            </div>
          </div>

          {/* Capacity */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Max Participants</Label>
              <span className="text-sm font-bold text-pop">{maxParticipants[0]}</span>
            </div>
            <Slider
              value={maxParticipants}
              onValueChange={setMaxParticipants}
              min={10}
              max={50}
              step={5}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>10 (Intimate)</span>
              <span>50 (Party)</span>
            </div>
          </div>

          {/* Video Toggle */}
          <div className="flex items-center justify-between p-4 rounded-xl bg-muted/50">
            <div className="flex items-center gap-3">
              {isVideoEnabled ? (
                <Video className="w-5 h-5 text-green-500" />
              ) : (
                <Mic className="w-5 h-5 text-blue-500" />
              )}
              <div>
                <p className="font-medium text-sm">
                  {isVideoEnabled ? "Video Booth" : "Voice Only"}
                </p>
                <p className="text-xs text-muted-foreground">
                  {isVideoEnabled ? "Cameras enabled for all" : "Audio only experience"}
                </p>
              </div>
            </div>
            <Switch
              checked={isVideoEnabled}
              onCheckedChange={setIsVideoEnabled}
            />
          </div>
        </div>

        {/* Create Button */}
        <Button
          onClick={handleCreate}
          disabled={!name.trim() || !topic.trim() || isCreating}
          className="w-full py-6 rounded-full bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-lg font-bold"
        >
          {isCreating ? (
            <>
              <div className="w-5 h-5 rounded-full border-2 border-white border-t-transparent animate-spin mr-2" />
              Creating...
            </>
          ) : (
            <>
              <Heart className="w-5 h-5 mr-2 fill-white" />
              Create Booth
            </>
          )}
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default CreateExhibitionBoothModal;